/* =========================================================
   Khotoua — interactions + mesure des conversions
   Port of khotoua-web/assets/js/khotoua.js. Same behaviour, but every
   listener/observer is removed by the returned cleanup function so the
   page can mount/unmount inside React.
   Compatible Google Tag Manager / GA4 (dataLayer), Meta Pixel (fbq), TikTok Pixel (ttq),
   Microsoft Clarity and Hotjar.
   ========================================================= */
import type { HomeLang } from './text';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    ttq?: { track?: (...args: unknown[]) => void };
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] };
    _hjSettings?: { hjid: number; hjsv: number };
    khotouaTrack?: (event: string, params?: Params) => void;
    khFitGroups?: () => void;
  }
}

/* ---------- 1. CONFIG : à compléter ---------- */
const CONFIG = {
  playStore: 'https://play.google.com/store/apps/details?id=com.DMNovaComany.Khotoua',
  appStore: '', // ex. https://apps.apple.com/app/id1234567890  (vide = « قريبًا »)
  appGallery: '', // ex. https://appgallery.huawei.com/app/C123456789 (vide = « قريبًا »)
  whatsapp: '21656078078',
  // The lead form posts to its `action` URL (Formgrid, see sections/Contact.tsx).
  // Without an action, it opens WhatsApp with the request already written.
  clarityId: 'z14vodydta', // Microsoft Clarity : ID du projet (clarity.microsoft.com › Settings › Overview)
  hotjarId: '', // Hotjar : Site ID (nombre, ex. 5123456) — Sites & Organizations
  gaId: 'G-QF2SCJ5C26', // Google Analytics 4 : ID de mesure « G-XXXXXXXXXX » (Admin › Flux de données › Web)
  askConsent: true, // true = bandeau « قبول / رفض » avant d'enregistrer les sessions (recommandé)
};
const PRICES_ALL: Record<HomeLang, Record<string, [string, string]>> = {
  ar: { tri: ['[السعر الثلاثي] د.ت', '/ الثلاثي'], sem: ['[السعر السداسي] د.ت', '/ السداسي'], year: ['[السعر السنوي] د.ت', '/ السنة'] },
  fr: { tri: ['[Prix trimestriel] DT', '/ trimestre'], sem: ['[Prix semestriel] DT', '/ semestre'], year: ['[Prix annuel] DT', '/ an'] },
  en: { tri: ['[Termly price] TND', '/ term'], sem: ['[6-month price] TND', '/ 6 months'], year: ['[Yearly price] TND', '/ year'] },
};

const I18N = {
  ar: {
    consent_label: 'الخصوصيّة',
    consent_text: 'نستعمل أدوات قياس (Google Analytics وMicrosoft Clarity وHotjar) لفهم كيف يتصفّح الزوّار الموقع وتحسين تجربتهم. لا نسجّل ما تكتبه في الاستمارة.',
    consent_privacy: 'سياسة الخصوصيّة', consent_accept: 'قبول', consent_refuse: 'رفض',
    who: { parent: 'وليّ', school: 'مدرسة', partner: 'مؤسّسة / شريك' } as Record<string, string>,
    wa_intro: 'مرحبا خطوة، أطلب عرضًا تجريبيًّا.', wa_who: 'أنا: ', wa_name: 'الاسم: ', wa_phone: 'الهاتف: ', wa_email: 'البريد: ',
    wa_level: 'المستوى: ', wa_school: 'المدرسة: ', wa_org: 'المؤسّسة: ',
    err_name: 'الرجاء إدخال الاسم واللقب.', err_phone: 'رقم هاتف غير صحيح (8 أرقام، مثال: 56 078 078).',
    err_email: 'الرجاء إدخال بريد إلكتروني صحيح.', err_level: 'الرجاء اختيار المستوى الدراسي.',
    err_school: 'الرجاء إدخال اسم المدرسة.', err_org: 'الرجاء إدخال اسم المؤسّسة.',
    err_all: 'الرجاء تعبئة جميع الحقول الإجباريّة (*) بشكل صحيح.',
    ok_whatsapp: 'شكرًا! أكمل الإرسال في واتساب وسنردّ عليك قريبًا.', sending: 'جارٍ الإرسال…',
    ok_sent: 'شكرًا! وصلنا طلبك وسيتّصل بك فريق خطوة قريبًا.',
    fail: 'تعذّر الإرسال حاليًّا. {wa} أو اتّصل بنا على 56 078 078.', fail_wa: 'أرسل طلبك عبر واتساب',
  },
  fr: {
    consent_label: 'Confidentialité',
    consent_text: 'Nous utilisons des outils de mesure (Google Analytics, Microsoft Clarity et Hotjar) pour comprendre comment les visiteurs utilisent le site et l’améliorer. Ce que vous tapez dans le formulaire n’est jamais enregistré.',
    consent_privacy: 'Politique de confidentialité', consent_accept: 'Accepter', consent_refuse: 'Refuser',
    who: { parent: 'Parent', school: 'École', partner: 'Institution / partenaire' } as Record<string, string>,
    wa_intro: 'Bonjour Khotoua, je souhaite une démo.', wa_who: 'Je suis : ', wa_name: 'Nom : ', wa_phone: 'Téléphone : ', wa_email: 'E-mail : ',
    wa_level: 'Niveau : ', wa_school: 'École : ', wa_org: 'Institution : ',
    err_name: 'Veuillez saisir votre nom et prénom.', err_phone: 'Numéro invalide (8 chiffres, ex. 56 078 078).',
    err_email: 'Veuillez saisir une adresse e-mail valide.', err_level: 'Veuillez choisir le niveau scolaire.',
    err_school: 'Veuillez saisir le nom de l’école.', err_org: 'Veuillez saisir le nom de l’institution.',
    err_all: 'Veuillez remplir correctement tous les champs obligatoires (*).',
    ok_whatsapp: 'Merci ! Terminez l’envoi dans WhatsApp, nous vous répondrons très vite.', sending: 'Envoi…',
    ok_sent: 'Merci ! Votre demande est bien arrivée, l’équipe Khotoua vous contactera très vite.',
    fail: 'L’envoi n’a pas abouti. {wa} ou appelez-nous au 56 078 078.', fail_wa: 'Envoyez votre demande par WhatsApp',
  },
  en: {
    consent_label: 'Privacy',
    consent_text: 'We use analytics tools (Google Analytics, Microsoft Clarity and Hotjar) to understand how visitors use the site and improve it. What you type in the form is never recorded.',
    consent_privacy: 'Privacy policy', consent_accept: 'Accept', consent_refuse: 'Decline',
    who: { parent: 'Parent', school: 'School', partner: 'Institution / partner' } as Record<string, string>,
    wa_intro: 'Hello Khotoua, I would like a demo.', wa_who: 'I am a: ', wa_name: 'Name: ', wa_phone: 'Phone: ', wa_email: 'Email: ',
    wa_level: 'Year: ', wa_school: 'School: ', wa_org: 'Institution: ',
    err_name: 'Please enter your full name.', err_phone: 'Invalid number (8 digits, e.g. 56 078 078).',
    err_email: 'Please enter a valid email address.', err_level: 'Please choose the school year.',
    err_school: 'Please enter the school name.', err_org: 'Please enter the institution name.',
    err_all: 'Please fill in all required fields (*) correctly.',
    ok_whatsapp: 'Thank you! Finish sending in WhatsApp and we will get back to you soon.', sending: 'Sending…',
    ok_sent: 'Thank you! We have received your request and the Khotoua team will contact you soon.',
    fail: 'Sending failed. {wa} or call us on +216 56 078 078.', fail_wa: 'Send your request via WhatsApp',
  },
};

type Params = Record<string, string | number>;

const UX_EVENTS: Record<string, 1> = {
  app_download_click: 1, demo_click: 1, whatsapp_click: 1, generate_lead: 1, lead_sent: 1, lead_send_failed: 1, form_start: 1, form_error: 1,
  parent_space_click: 1, store_coming_soon_click: 1, feature_tab_select: 1, pricing_toggle: 1, contact_click: 1, social_click: 1, scroll_depth: 1,
};

/* ---------- 2b. Expérience visiteurs : Microsoft Clarity + Hotjar + GA4 (avec consentement) ----------
   Third-party scripts load once per document, whatever the number of mounts. */
const CONSENT_KEY = 'kh_consent_v1';
function getConsent() { try { return localStorage.getItem(CONSENT_KEY); } catch (e) { return null; } }
function setConsent(v: string) { try { localStorage.setItem(CONSENT_KEY, v); } catch (e) {} }
let clarityLoaded = false, hotjarLoaded = false, gaLoaded = false;

export function initKhotoua(root: HTMLElement, LANG: HomeLang): () => void {
  const cleanups: Array<() => void> = [];
  const on = (el: EventTarget, type: string, fn: (e: any) => void, opts?: AddEventListenerOptions) => {
    el.addEventListener(type, fn, opts);
    cleanups.push(() => el.removeEventListener(type, fn, opts));
  };
  const timers: Array<ReturnType<typeof setTimeout>> = [];
  const later = (fn: () => void, ms: number) => { timers.push(setTimeout(fn, ms)); };
  cleanups.push(() => timers.forEach(clearTimeout));
  let disposed = false;
  cleanups.push(() => { disposed = true; });
  const $ = <T extends Element = HTMLElement>(sel: string) => root.querySelector<T>(sel);
  const $$ = <T extends Element = HTMLElement>(sel: string) => Array.from(root.querySelectorAll<T>(sel));

  document.documentElement.classList.add('js');
  cleanups.push(() => document.documentElement.classList.remove('js'));

  /* ---------- 1b. Langue de la page (ar / fr / en) ---------- */
  const RTL = LANG === 'ar';
  const PRICES = PRICES_ALL[LANG] || PRICES_ALL.ar;
  const T = I18N[LANG];

  /* ---------- 2. Tracking ---------- */
  window.dataLayer = window.dataLayer || [];
  function track(event: string, params: Params = {}) {
    params.page_language = LANG;
    window.dataLayer.push(Object.assign({ event }, params));
    try { if (typeof window.gtag === 'function') window.gtag('event', event, params); } catch (e) {}
    try {
      if (typeof window.fbq === 'function') {
        if (event === 'app_download_click' || event === 'generate_lead') window.fbq('track', 'Lead', { content_name: event + '_' + (params.store || params.profile || '') });
        else if (event === 'whatsapp_click') window.fbq('track', 'Contact');
        else window.fbq('trackCustom', event, params);
      }
    } catch (e) {}
    try { if (window.ttq && window.ttq.track) window.ttq.track(event === 'app_download_click' ? 'Download' : event, params); } catch (e) {}
    // Clarity / Hotjar : les événements servent à filtrer les enregistrements de sessions et les cartes de chaleur
    if (UX_EVENTS[event]) {
      try { if (typeof window.clarity === 'function') { window.clarity('event', event); if (params.placement) window.clarity('set', 'last_cta', params.placement); if (event === 'generate_lead' || event === 'app_download_click') window.clarity('upgrade', event); } } catch (e) {}
      try { if (typeof window.hj === 'function') window.hj('event', event); } catch (e) {}
    }
  }
  window.khotouaTrack = track;

  /* ---------- 3. UTM conservés pendant la visite ---------- */
  const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'ttclid'];
  let utm: Record<string, string> = {};
  try { utm = JSON.parse(sessionStorage.getItem('kh_utm') || '{}'); } catch (e) {}
  const qs = new URLSearchParams(window.location.search);
  KEYS.forEach((k) => { const v = qs.get(k); if (v) utm[k] = v; });
  if (!utm.utm_source && document.referrer && document.referrer.indexOf(window.location.hostname) === -1) {
    try { utm.utm_source = new URL(document.referrer).hostname; utm.utm_medium = 'referral'; } catch (e) {}
  }
  if (!utm.utm_source) { utm.utm_source = 'khotoua.com'; utm.utm_medium = 'website'; }
  try { sessionStorage.setItem('kh_utm', JSON.stringify(utm)); } catch (e) {}

  /* ---------- 2b (suite). Chargement des outils selon le consentement ---------- */
  function loadClarity() {
    if (clarityLoaded || !CONFIG.clarityId) return; clarityLoaded = true;
    const c = window as any;
    c.clarity = c.clarity || function (...args: unknown[]) { (c.clarity.q = c.clarity.q || []).push(args); };
    const t = document.createElement('script'); t.async = true; t.src = 'https://www.clarity.ms/tag/' + CONFIG.clarityId;
    const y = document.getElementsByTagName('script')[0]; y.parentNode!.insertBefore(t, y);
    window.clarity!('set', 'page_language', LANG);
    if (utm.utm_source) window.clarity!('set', 'utm_source', utm.utm_source);
    if (utm.utm_campaign) window.clarity!('set', 'utm_campaign', utm.utm_campaign);
  }
  function loadHotjar() {
    if (hotjarLoaded || !CONFIG.hotjarId) return; hotjarLoaded = true;
    const h = window as any;
    h.hj = h.hj || function (...args: unknown[]) { (h.hj.q = h.hj.q || []).push(args); };
    h._hjSettings = { hjid: +CONFIG.hotjarId, hjsv: 6 };
    const r = document.createElement('script'); r.async = true;
    r.src = 'https://static.hotjar.com/c/hotjar-' + h._hjSettings.hjid + '.js?sv=' + h._hjSettings.hjsv;
    document.head.appendChild(r);
  }
  function loadGA(v: string) {
    if (!CONFIG.gaId) return;
    // gtag must push the `arguments` object itself, not an array
    // eslint-disable-next-line prefer-rest-params
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    if (!gaLoaded) {
      gaLoaded = true;
      // Consent Mode v2 : sans accord, GA4 envoie des mesures anonymes sans cookie (modélisation)
      window.gtag('consent', 'default', { analytics_storage: v === 'granted' ? 'granted' : 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', wait_for_update: 500 });
      const sc = document.createElement('script'); sc.async = true;
      sc.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(CONFIG.gaId);
      document.head.appendChild(sc);
      window.gtag('js', new Date());
      window.gtag('config', CONFIG.gaId, { page_language: LANG, content_group: 'home_' + LANG }); // les UTM de l'URL sont lus par GA4 lui-même
    } else {
      window.gtag('consent', 'update', { analytics_storage: v === 'granted' ? 'granted' : 'denied' });
    }
  }
  function applyConsent(v: string) {
    loadGA(v);
    // Clarity : chargé dans tous les cas en mode « sans cookie » ; les cookies et le suivi entre visites seulement après accord.
    loadClarity();
    if (window.clarity) window.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: v === 'granted' ? 'granted' : 'denied' });
    // Hotjar : chargé uniquement après accord.
    if (v === 'granted') loadHotjar();
  }
  function consentBanner() {
    if (disposed || document.getElementById('kh-consent')) return;
    const box = document.createElement('div');
    box.id = 'kh-consent'; box.setAttribute('role', 'dialog'); box.setAttribute('aria-live', 'polite'); box.setAttribute('aria-label', T.consent_label);
    box.innerHTML = '<p>' + T.consent_text + ' <a href="/privacy" target="_blank" rel="noopener">' + T.consent_privacy + '</a></p>' +
      '<div><button type="button" data-consent="granted">' + T.consent_accept + '</button><button type="button" data-consent="denied">' + T.consent_refuse + '</button></div>';
    document.body.appendChild(box);
    box.addEventListener('click', (e) => {
      const b = (e.target as Element).closest('[data-consent]'); if (!b) return;
      const v = b.getAttribute('data-consent') || 'denied';
      const was = getConsent();
      setConsent(v); box.remove();
      if (was === 'granted' && v === 'denied') { try { if (window.clarity) window.clarity('consent', false); } catch (er) {} window.location.reload(); return; }
      applyConsent(v);
      track('consent_choice', { choice: v });
    });
  }
  cleanups.push(() => { document.getElementById('kh-consent')?.remove(); });
  if (CONFIG.clarityId || CONFIG.hotjarId || CONFIG.gaId) {
    const c0 = getConsent();
    if (!CONFIG.askConsent) applyConsent('granted');
    else { applyConsent(c0 || 'denied'); if (!c0) later(consentBanner, 1200); }
    on(root, 'click', (e: MouseEvent) => { if ((e.target as Element).closest('[data-consent-open]')) { e.preventDefault(); consentBanner(); } });
  } else {
    $$('[data-consent-open]').forEach((a) => { a.hidden = true; });
  }
  // Données personnelles du formulaire : jamais enregistrées par Clarity ni Hotjar
  $$('#kh-lead input, #kh-lead select').forEach((f) => {
    f.setAttribute('data-clarity-mask', 'True'); f.setAttribute('data-hj-suppress', '');
  });

  function placementOf(el: Element) {
    if (el.closest('header')) return 'header';
    if (el.closest('footer')) return 'footer';
    const s = el.closest('section[id]');
    if (s) return s.id === 'top' ? 'hero' : s.id;
    if (el.closest('.kh-wa-float')) return 'floating';
    return 'page';
  }

  /* Liens Google Play : referrer d'installation (visible dans Play Console › Acquisition) */
  $$<HTMLAnchorElement>('a[href*="play.google.com"]').forEach((a) => {
    const p = placementOf(a);
    const ref = 'utm_source=' + encodeURIComponent(utm.utm_source) + '&utm_medium=' + encodeURIComponent(utm.utm_medium || 'website') +
      '&utm_campaign=' + encodeURIComponent((utm.utm_campaign || 'site') + '_' + p);
    try { const u = new URL(CONFIG.playStore); u.searchParams.set('pcampaignid', 'web_share'); u.searchParams.set('referrer', ref); a.href = u.toString(); } catch (e) {}
    a.setAttribute('data-store', 'android');
  });
  /* App Store / AppGallery : vrais liens si renseignés, sinon « قريبًا » */
  ([['#appstore', CONFIG.appStore, 'ios'], ['#appgallery', CONFIG.appGallery, 'huawei']] as const).forEach(([hash, url, store]) => {
    $$<HTMLAnchorElement>('a[href="' + hash + '"]').forEach((a) => {
      a.setAttribute('data-store', store);
      if (url) { a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.removeAttribute('data-soon'); }
    });
  });

  /* ---------- 4. Toast « قريبا … » ---------- */
  const toast = $('#kh-soon'), toastMsg = $('#kh-soon-msg');
  let toastT: ReturnType<typeof setTimeout> | undefined;
  cleanups.push(() => clearTimeout(toastT));
  function showSoon(msg: string | null) {
    if (!toast) return;
    if (toastMsg && msg) toastMsg.textContent = msg;
    toast.hidden = true; void toast.offsetWidth; toast.hidden = false;
    clearTimeout(toastT);
    toastT = setTimeout(() => { toast.hidden = true; }, 2600);
  }

  /* ---------- 5. Clics : actions + mesure ---------- */
  on(root, 'click', (e: MouseEvent) => {
    const el = (e.target as Element).closest('a,button');
    if (!el) return;
    const placement = placementOf(el);
    const href = el.getAttribute('href') || '';
    const store = el.getAttribute('data-store');

    if (el.hasAttribute('data-soon')) {
      e.preventDefault();
      showSoon(el.getAttribute('data-soon'));
      track(el.getAttribute('data-track') || (store ? 'store_coming_soon_click' : 'coming_soon_click'), { store: store || '', placement });
      return;
    }
    if (store) { track('app_download_click', { store, placement }); return; }
    if (href.indexOf('KHOTOUA_DEMO') > -1) { track('demo_click', { placement }); return; }
    if (href.indexOf('wa.me') > -1) { track('whatsapp_click', { placement }); return; }
    if (href.indexOf('tel:') === 0) { track('contact_click', { method: 'phone' }); return; }
    if (href.indexOf('mailto:') === 0) { track('contact_click', { method: 'email' }); return; }
    const net = href.match(/instagram|facebook|tiktok|linkedin|youtube/);
    if (net) { track('social_click', { network: net[0] }); return; }
    if (href.indexOf('dmnova') > -1) track('outbound_click', { target: 'dmnova' });
  });

  /* ---------- 6. Menu mobile ---------- */
  const burger = $('.kh-burger'), menu = $('#kh-menu');
  if (burger && menu) {
    on(burger, 'click', () => {
      const open = menu.classList.toggle('open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    on(menu, 'click', (e: MouseEvent) => { if ((e.target as Element).closest('a')) { menu.classList.remove('open'); burger.setAttribute('aria-expanded', 'false'); } });
  }

  /* ---------- 7. Onglets « مميزات التطبيق » ---------- */
  function tabStyle(isOn: boolean) {
    return 'min-height: 48px; padding: 0 20px; border-radius: 999px; cursor: pointer; font-weight: 700; font-size: 15px; border: 2px solid ' +
      (isOn ? '#141C63' : '#E1E5F7') + '; background: ' + (isOn ? '#141C63' : '#FFFFFF') + '; color: ' + (isOn ? '#FFFFFF' : '#121A4A');
  }
  const tabs = $$('[data-tab]');
  function pickTab(i: number, focus?: boolean) {
    tabs.forEach((t, k) => {
      const isOn = k === i;
      t.setAttribute('aria-selected', isOn ? 'true' : 'false');
      t.setAttribute('tabindex', isOn ? '0' : '-1');
      t.setAttribute('style', tabStyle(isOn));
      if (isOn && focus) t.focus();
    });
    $$('[data-panel]').forEach((p) => { p.hidden = +(p.getAttribute('data-panel') || 0) !== i; });
    fitGroups();
  }
  tabs.forEach((t, i) => {
    t.setAttribute('tabindex', i === 0 ? '0' : '-1');
    on(t, 'click', () => { pickTab(i); track('feature_tab_select', { tab: (t.textContent || '').trim() }); });
    on(t, 'keydown', (e: KeyboardEvent) => {
      const d = e.key === 'ArrowLeft' ? (RTL ? 1 : -1) : e.key === 'ArrowRight' ? (RTL ? -1 : 1) : 0;
      if (d) { e.preventDefault(); pickTab((i + d + tabs.length) % tabs.length, true); }
    });
  });

  /* ---------- 8. Abonnements : ثلاثي / سداسي / سنوي ---------- */
  function pillStyle(isOn: boolean) {
    return 'min-height: 44px; padding: 0 20px; border-radius: 999px; border: 0; cursor: pointer; font-weight: 700; background: ' +
      (isOn ? '#141C63' : 'transparent') + '; color: ' + (isOn ? '#FFFFFF' : '#121A4A');
  }
  const price = $('#kh-price'), period = $('#kh-period');
  const bills = $$('[data-bill]');
  bills.forEach((btn) => {
    on(btn, 'click', () => {
      const k = btn.getAttribute('data-bill') || '';
      bills.forEach((o) => {
        const isOn = o === btn;
        o.setAttribute('style', pillStyle(isOn));
        o.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      });
      if (PRICES[k]) { if (price) price.textContent = PRICES[k][0]; if (period) period.textContent = PRICES[k][1]; }
      track('pricing_toggle', { billing: k });
    });
  });

  /* ---------- 9. Formulaire : وليّ / مدرسة / مؤسّسة ---------- */
  type Field = HTMLInputElement | HTMLSelectElement;
  function radioStyle(isOn: boolean) {
    return 'display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 16px; border-radius: 999px; border: 2px solid ' +
      (isOn ? '#141C63' : '#E1E5F7') + '; font-weight: 600; cursor: pointer';
  }
  const form = $<HTMLFormElement>('#kh-lead');
  function who() { const r = form && form.querySelector<HTMLInputElement>('input[name="who"]:checked'); return r ? r.value : 'parent'; }
  function fieldErr(f: Element, text: string) {
    f.setAttribute('aria-invalid', 'true');
    const lab = f.closest('label');
    let e = lab && lab.querySelector('.kh-err');
    if (lab && !e) { e = document.createElement('span'); e.className = 'kh-err'; e.setAttribute('role', 'alert'); lab.appendChild(e); }
    if (e) e.textContent = text;
  }
  function clearErr(f: Element) {
    f.removeAttribute('aria-invalid');
    const lab = f.closest('label'), e = lab && lab.querySelector('.kh-err');
    if (e) e.remove();
  }
  function syncWho() {
    const w = who();
    $$('[data-who-label]').forEach((l) => { l.setAttribute('style', radioStyle(l.getAttribute('data-who-label') === w)); });
    $$('[data-who-panel]').forEach((p) => { p.hidden = p.getAttribute('data-who-panel') !== w; });
    if (form) form.querySelectorAll('[data-req-when]').forEach((f) => { clearErr(f); });
  }
  if (form) {
    form.querySelectorAll('input[name="who"]').forEach((r) => on(r, 'change', syncWho));
    syncWho();
    let started = false;
    on(form, 'input', (e: Event) => {
      if (!started) { started = true; track('form_start', { profile: who() }); }
      const target = e.target as Element;
      if (target.getAttribute('aria-invalid') === 'true') clearErr(target);
    });
    on(form, 'change', (e: Event) => { const target = e.target as Element; if (target.tagName === 'SELECT') clearErr(target); });
    const msg = $('#kh-form-msg');
    const say = (html: string, ok: boolean) => {
      if (!msg) return;
      msg.hidden = false; msg.innerHTML = html;
      msg.style.background = ok ? '#E2F7F3' : '#FDECEC'; msg.style.color = ok ? '#145E55' : '#9B1C1F';
    };
    const waLink = (data: Record<string, string>) => {
      const lines = [T.wa_intro, T.wa_who + T.who[data.profile], T.wa_name + data.name, T.wa_phone + data.phone, T.wa_email + data.email];
      if (data.school_level) lines.push(T.wa_level + data.school_level);
      if (data.school_name) lines.push(T.wa_school + data.school_name);
      if (data.organization) lines.push(T.wa_org + data.organization);
      return 'https://wa.me/' + CONFIG.whatsapp + '?text=' + encodeURIComponent(lines.join('\n'));
    };

    on(form, 'submit', (e: SubmitEvent) => {
      e.preventDefault();
      const field = (n: string) => form.elements.namedItem(n) as Field | null;
      const w = who(), bad: Array<[Field, string]> = [];
      const name = field('name')!, phone = field('phone')!, email = field('email')!;
      const level = field('level'), school = field('school'), organization = field('organization'), website = field('website');
      const digits = phone.value.replace(/[\s().-]/g, '').replace(/^\+/, '').replace(/^00/, '');

      if (name.value.trim().length < 3) bad.push([name, T.err_name]);
      if (!/^\d{8}$|^216\d{8}$|^\d{9,15}$/.test(digits)) bad.push([phone, T.err_phone]);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value.trim())) bad.push([email, T.err_email]);
      if (w === 'parent' && level && !level.value) bad.push([level, T.err_level]);
      if (w === 'school' && school && school.value.trim().length < 2) bad.push([school, T.err_school]);
      if (w === 'partner' && organization && organization.value.trim().length < 2) bad.push([organization, T.err_org]);

      [name, phone, email, level, school, organization].forEach((f) => { if (f) clearErr(f); });
      if (bad.length) {
        bad.forEach(([f, text]) => fieldErr(f, text));
        say(T.err_all, false);
        bad[0][0].focus();
        track('form_error', { profile: w, fields: bad.map(([f]) => f.name).join(',') });
        return;
      }
      if (website && website.value) return; // robot (champ piège)

      const data: Record<string, string> = {
        profile: w, profile_ar: I18N.ar.who[w], lang: LANG,
        name: name.value.trim(), phone: phone.value.trim(), email: email.value.trim(),
        school_level: w === 'parent' && level ? level.value : '',
        school_name: w === 'school' && school ? school.value.trim() : '',
        organization: w === 'partner' && organization ? organization.value.trim() : '',
        page: window.location.href, sent_at: new Date().toISOString(),
      };
      Object.keys(utm).forEach((k) => { data[k] = utm[k]; });
      track('generate_lead', { profile: w, school_level: data.school_level, school_name: data.school_name, organization: data.organization });

      const endpoint = form.getAttribute('action');
      if (!endpoint) {
        // Pas de réception configurée : la demande part sur WhatsApp, déjà rédigée.
        window.open(waLink(data), '_blank', 'noopener');
        say(T.ok_whatsapp, true);
        return;
      }
      const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]')!, label = btn.textContent;
      btn.disabled = true; btn.textContent = T.sending;
      // Same encoding as a native form POST, so no CORS preflight is needed
      fetch(endpoint, { method: 'POST', headers: { Accept: 'application/json' }, body: new URLSearchParams(data) })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json().catch(() => ({ ok: true })); })
        .then((res) => {
          if (res && res.ok === false) throw new Error(res.error || 'error');
          say(T.ok_sent, true);
          track('lead_sent', { profile: w });
          form.reset(); syncWho();
        })
        .catch(() => {
          say(T.fail.replace('{wa}', '<a href="' + waLink(data) + '" target="_blank" rel="noopener" style="color:inherit;font-weight:800">' + T.fail_wa + '</a>'), false);
          track('lead_send_failed', { profile: w });
        })
        .then(() => { btn.disabled = false; btn.textContent = label; });
    });
  }

  /* ---------- 10. Flèche « تابع النزول » + profondeur de défilement ---------- */
  const next = $('#kh-next');
  const nextBtn = $('[data-scroll-next]');
  if (nextBtn) on(nextBtn, 'click', () => { window.scrollBy({ top: Math.round(window.innerHeight * 0.85), behavior: 'smooth' }); });
  const marks = [25, 50, 75, 90], hit: Record<number, boolean> = {};
  function onScroll() {
    const d = document.documentElement, y = window.scrollY || d.scrollTop;
    if (next) next.classList.toggle('kh-hide', y + window.innerHeight >= d.scrollHeight - 160);
    const pct = Math.round((y + window.innerHeight) / d.scrollHeight * 100);
    marks.forEach((m) => { if (pct >= m && !hit[m]) { hit[m] = true; track('scroll_depth', { percent: m }); } });
  }
  on(window, 'scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- 10b. Bloc « écran » : réduit d'un seul tenant sur petit écran ---------- */
  const DESIGN_W = 760, NATURAL_MIN = 980;
  function fitScreens() {
    if (disposed) return;
    $$('.kh-screen').forEach((sc) => {
      const view = sc.querySelector<HTMLElement>('.kh-screen-view'), stage = sc.querySelector<HTMLElement>('.kh-screen-stage');
      if (!view || !stage) return;
      const cs = getComputedStyle(view);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight), padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const avail = view.clientWidth - padX;
      if (avail >= NATURAL_MIN) {
        sc.classList.remove('is-scaled'); stage.style.width = ''; stage.style.transform = ''; view.style.height = '';
        return;
      }
      sc.classList.add('is-scaled');
      stage.style.width = DESIGN_W + 'px';
      const s = Math.min(1, avail / DESIGN_W);
      stage.style.transform = 'scale(' + s + ')';
      view.style.height = Math.ceil(stage.offsetHeight * s + padY) + 'px';
    });
  }
  function fitGroups() {
    if (disposed) return;
    $$('.kh-fit').forEach((box) => {
      const inner = box.querySelector<HTMLElement>('.kh-fit-inner');
      if (!inner || box.offsetParent === null) return;
      inner.style.transform = ''; box.style.height = '';
      const avail = box.clientWidth, w = inner.offsetWidth;
      if (avail > 0 && avail < w) {
        const s = avail / w;
        inner.style.transform = 'scale(' + s + ')';
        box.style.height = Math.ceil(inner.offsetHeight * s) + 'px';
      }
    });
  }
  window.khFitGroups = fitGroups;
  cleanups.push(() => { if (window.khFitGroups === fitGroups) delete window.khFitGroups; });
  fitScreens(); fitGroups();
  on(window, 'resize', fitGroups);
  on(window, 'load', fitGroups);
  on(window, 'resize', fitScreens);
  on(window, 'load', fitScreens);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitScreens);
  if ('ResizeObserver' in window) {
    const rso = new ResizeObserver(fitScreens);
    $$('.kh-screen-stage').forEach((st) => rso.observe(st));
    cleanups.push(() => rso.disconnect());
  }

  /* ---------- 11. Vues de sections + apparition douce ---------- */
  if ('IntersectionObserver' in window) {
    const seen: Record<string, boolean> = {};
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting && !seen[en.target.id]) { seen[en.target.id] = true; track('section_view', { section: en.target.id }); }
      });
    }, { threshold: 0.3 });
    $$('section[id], footer[id]').forEach((s) => io.observe(s));
    cleanups.push(() => io.disconnect());

    const targets = $$('#about > div, #contents figure, #demo figure, #features > div > div:first-child, #trust article, #trust blockquote, #pricing article, #contact form, #schools article, #pedagogy article');
    const ro = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('kh-in'); ro.unobserve(en.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    targets.forEach((t) => {
      if (t.getBoundingClientRect().top < window.innerHeight) return; // déjà visible : pas d'effet
      t.classList.add('kh-reveal'); ro.observe(t);
    });
    cleanups.push(() => {
      ro.disconnect();
      targets.forEach((t) => t.classList.remove('kh-reveal', 'kh-in'));
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
